import type { AdminFeature } from '@lib/features/types'
import {
  emptyFeatureFormValues,
  type FeatureFormValues,
  type FeaturePermissionRow
} from '@lib/features/types'
import {
  ActionIcon,
  Accordion,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { openConfirmModal } from '@packages/ui'
import {
  IconEdit,
  IconPlus,
  IconTrash
} from '@tabler/icons-react'
import { useMemo, useState } from 'react'
import {
  PermissionFormModal,
  permissionFromRow
} from './PermissionFormModal'

export type FeatureWithPermissions = AdminFeature & {
  permissions: Array<{
    id: string
    name: string
    description: string | null
    disabled: boolean
  }>
}

export function buildFeaturesWithPermissions(
  features: AdminFeature[],
  rows: FeaturePermissionRow[]
): FeatureWithPermissions[] {
  const permMap = new Map<
    string,
    FeatureWithPermissions['permissions']
  >()

  for (const row of rows) {
    const perm = permissionFromRow(row)
    if (!perm) continue
    const list = permMap.get(row.feature_id) ?? []
    if (!list.some((p) => p.id === perm.id)) {
      list.push(perm)
    }
    permMap.set(row.feature_id, list)
  }

  return features
    .map((feature) => ({
      ...feature,
      permissions: (permMap.get(feature.id) ?? []).sort((a, b) =>
        a.name.localeCompare(b.name)
      )
    }))
    .sort(
      (a, b) => a.sort_order - b.sort_order || a.key.localeCompare(b.key)
    )
}

type FeaturePermissionPanelProps = {
  tenantTypeLabel: string
  features: FeatureWithPermissions[]
  enabledKeys: Set<string>
  hasDraft: boolean
  saving: boolean
  loading: boolean
  onToggleFeature: (featureKey: string, enabled: boolean) => void
  onSaveIcp: () => Promise<void>
  onDiscardIcp: () => void
  onUpsertFeature: (values: FeatureFormValues, editingId: string | null) => Promise<void>
  onDeleteFeature: (feature: AdminFeature) => Promise<void>
  onCreatePermission: (
    featureId: string,
    values: { name: string; description: string; disabled: boolean }
  ) => Promise<void>
  onUpdatePermission: (
    permissionId: string,
    values: { description: string; disabled: boolean }
  ) => Promise<void>
}

export function FeaturePermissionPanel({
  tenantTypeLabel,
  features,
  enabledKeys,
  hasDraft,
  saving,
  loading,
  onToggleFeature,
  onSaveIcp,
  onDiscardIcp,
  onUpsertFeature,
  onDeleteFeature,
  onCreatePermission,
  onUpdatePermission
}: FeaturePermissionPanelProps) {
  const [featureFormOpened, featureFormHandlers] = useDisclosure(false)
  const [permissionModalOpened, permissionModalHandlers] = useDisclosure(false)
  const [editingFeature, setEditingFeature] = useState<AdminFeature | null>(
    null
  )
  const [editingPermission, setEditingPermission] = useState<
    FeatureWithPermissions['permissions'][number] | null
  >(null)
  const [permissionFeature, setPermissionFeature] =
    useState<AdminFeature | null>(null)
  const [formValues, setFormValues] =
    useState<FeatureFormValues>(emptyFeatureFormValues)
  const [formSaving, setFormSaving] = useState(false)
  const [permissionSaving, setPermissionSaving] = useState(false)

  const activeFeatures = useMemo(
    () => features.filter((f) => !f.disabled),
    [features]
  )

  const openCreateFeature = () => {
    setEditingFeature(null)
    setFormValues(emptyFeatureFormValues)
    featureFormHandlers.open()
  }

  const openEditFeature = (feature: AdminFeature) => {
    setEditingFeature(feature)
    setFormValues({
      key: feature.key,
      name: feature.name,
      description: feature.description || '',
      sort_order: feature.sort_order,
      disabled: feature.disabled
    })
    featureFormHandlers.open()
  }

  const openCreatePermission = (feature: AdminFeature) => {
    setPermissionFeature(feature)
    setEditingPermission(null)
    permissionModalHandlers.open()
  }

  const openEditPermission = (
    feature: AdminFeature,
    permission: FeatureWithPermissions['permissions'][number]
  ) => {
    setPermissionFeature(feature)
    setEditingPermission(permission)
    permissionModalHandlers.open()
  }

  const submitFeature = async () => {
    if (!formValues.key.trim() || !formValues.name.trim()) return
    setFormSaving(true)
    try {
      await onUpsertFeature(formValues, editingFeature?.id ?? null)
      featureFormHandlers.close()
      setEditingFeature(null)
      setFormValues(emptyFeatureFormValues)
    } finally {
      setFormSaving(false)
    }
  }

  const confirmDeleteFeature = (feature: AdminFeature) => {
    openConfirmModal({
      title: 'Delete feature',
      children: (
        <Text size="sm">
          Delete feature <strong>{feature.key}</strong>? This removes ICP and
          tenant custom assignments for this feature.
        </Text>
      ),
      confirmLabel: 'Delete',
      confirmButtonProps: { color: 'red' },
      onConfirm: () => onDeleteFeature(feature)
    })
  }

  const submitPermission = async (values: {
    name: string
    description: string
    disabled: boolean
  }) => {
    if (!permissionFeature) return
    setPermissionSaving(true)
    try {
      if (editingPermission) {
        await onUpdatePermission(editingPermission.id, {
          description: values.description,
          disabled: values.disabled
        })
      } else {
        await onCreatePermission(permissionFeature.id, values)
      }
      permissionModalHandlers.close()
      setEditingPermission(null)
      setPermissionFeature(null)
    } finally {
      setPermissionSaving(false)
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Text fw={600}>Features &amp; permissions</Text>
          <Text size="sm" c="dimmed">
            ICP ceiling for {tenantTypeLabel}. Toggle modules on the left column
            context; permissions belong to each feature.
          </Text>
        </Stack>
        <Group gap="xs">
          <Button
            variant="default"
            leftSection={<IconPlus size={16} />}
            onClick={openCreateFeature}
          >
            Add feature
          </Button>
          <Button
            loading={saving}
            disabled={!hasDraft}
            onClick={() => void onSaveIcp()}
          >
            Save ICP
          </Button>
          {hasDraft && (
            <Button variant="default" onClick={onDiscardIcp}>
              Discard
            </Button>
          )}
        </Group>
      </Group>

      {loading ? (
        <Text size="sm" c="dimmed">
          Loading…
        </Text>
      ) : activeFeatures.length === 0 ? (
        <Text size="sm" c="dimmed">
          No active features in the catalog.
        </Text>
      ) : (
        <Accordion
          variant="separated"
          multiple
          className="admin-feature-catalog-accordion text-black"
        >
          {activeFeatures.map((feature) => {
            const icpEnabled = enabledKeys.has(feature.key)

            return (
              <Accordion.Item key={feature.id} value={feature.id}>
                <Accordion.Control>
                  <Group justify="space-between" wrap="nowrap" pr="sm">
                    <Stack gap={2}>
                      <Group gap="xs">
                        <Text fw={600}>{feature.name}</Text>
                        <Text size="xs" c="dimmed" ff="monospace">
                          {feature.key}
                        </Text>
                        {!icpEnabled && (
                          <Badge size="xs" variant="light" color="gray">
                            Off for ICP
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {feature.permissions.length} permission
                        {feature.permissions.length === 1 ? '' : 's'}
                      </Text>
                    </Stack>
                    <Group gap={4} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={icpEnabled}
                        onChange={(e) =>
                          onToggleFeature(
                            feature.key,
                            e.currentTarget.checked
                          )
                        }
                        aria-label={`Enable ${feature.key} for ${tenantTypeLabel}`}
                        label="ICP"
                        size="sm"
                      />
                      <ActionIcon
                        variant="subtle"
                        aria-label={`Edit feature ${feature.key}`}
                        onClick={() => openEditFeature(feature)}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={`Delete feature ${feature.key}`}
                        onClick={() => confirmDeleteFeature(feature)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    {feature.description && (
                      <Text size="sm" c="dimmed">
                        {feature.description}
                      </Text>
                    )}
                    <Group justify="space-between">
                      <Text size="sm" fw={500}>
                        Permissions
                      </Text>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => openCreatePermission(feature)}
                      >
                        Add permission
                      </Button>
                    </Group>
                    {feature.permissions.length === 0 ? (
                      <Text size="sm" c="dimmed">
                        No permissions linked to this feature yet.
                      </Text>
                    ) : (
                      <Stack gap="xs">
                        {feature.permissions.map((perm) => (
                          <Group
                            key={perm.id}
                            justify="space-between"
                            wrap="nowrap"
                            className="rounded border border-gray-200 px-3 py-2 dark:border-gray-700"
                          >
                            <Stack gap={2}>
                              <Group gap="xs">
                                <Text ff="monospace" size="sm">
                                  {perm.name}
                                </Text>
                                {perm.disabled && (
                                  <Badge size="xs" color="gray">
                                    Disabled
                                  </Badge>
                                )}
                              </Group>
                              {perm.description && (
                                <Text size="xs" c="dimmed">
                                  {perm.description}
                                </Text>
                              )}
                            </Stack>
                            <ActionIcon
                              variant="subtle"
                              aria-label={`Edit permission ${perm.name}`}
                              onClick={() => openEditPermission(feature, perm)}
                            >
                              <IconEdit size={16} />
                            </ActionIcon>
                          </Group>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )
          })}
        </Accordion>
      )}

      <Modal
        opened={featureFormOpened}
        onClose={featureFormHandlers.close}
        title={editingFeature ? 'Edit feature' : 'Add feature'}
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Key"
            description="Lowercase identifier; matches permission prefix (e.g. chats)"
            value={formValues.key}
            onChange={(e) =>
              setFormValues((v) => ({ ...v, key: e.target.value }))
            }
            disabled={Boolean(editingFeature)}
            required
          />
          <TextInput
            label="Name"
            value={formValues.name}
            onChange={(e) =>
              setFormValues((v) => ({ ...v, name: e.target.value }))
            }
            required
          />
          <Textarea
            label="Description"
            value={formValues.description}
            onChange={(e) =>
              setFormValues((v) => ({ ...v, description: e.target.value }))
            }
            minRows={2}
          />
          <NumberInput
            label="Sort order"
            value={formValues.sort_order}
            onChange={(value) =>
              setFormValues((v) => ({
                ...v,
                sort_order: typeof value === 'number' ? value : 0
              }))
            }
          />
          <Switch
            label="Globally disabled"
            description="Hides from ICP matrix and tenant customization"
            checked={formValues.disabled}
            onChange={(e) =>
              setFormValues((v) => ({
                ...v,
                disabled: e.currentTarget.checked
              }))
            }
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={featureFormHandlers.close}>
              Cancel
            </Button>
            <Button loading={formSaving} onClick={() => void submitFeature()}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      {permissionFeature && (
        <PermissionFormModal
          feature={permissionFeature}
          permission={editingPermission}
          opened={permissionModalOpened}
          onClose={() => {
            permissionModalHandlers.close()
            setEditingPermission(null)
            setPermissionFeature(null)
          }}
          onSubmit={submitPermission}
          saving={permissionSaving}
        />
      )}
    </Stack>
  )
}
